import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
  Collapse,
  Switch,
  TextField,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  VpnKey as VpnKeyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  WifiOff as WifiOffIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { getTenants, deleteTenant, updateTenant } from "../../services/tenantService";

const API_BASE =
  process.env.REACT_APP_SEAM_SERVER_URL ||
  process.env.REACT_APP_API_URL ||
  "http://localhost:8000";

// ─── helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString();
};

const isLeaseActive = (startDate, endDate) => {
  const now = new Date();
  return new Date(startDate) <= now && new Date(endDate) >= now;
};

const formatExpiresAt = (isoStr) => {
  if (!isoStr) return "—";
  return new Date(isoStr).toLocaleString();
};

// ─── sub-component: permanent-codes panel (view only) ────────────────────────

const PermanentCodesPanel = ({ propertyId }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(null);

  const fetchCodes = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/tenant/access-codes/property/${propertyId}?type=permanent`
      );
      if (res.ok) {
        const data = await res.json();
        setCodes(data.codes || []);
      }
    } catch (err) {
      console.error("[PermanentCodesPanel] fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    if (expanded) fetchCodes();
  }, [expanded, fetchCodes]);

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(code);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <Box sx={{ mt: 3, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover" },
          borderRadius: expanded ? "8px 8px 0 0" : 2,
        }}
        onClick={() => setExpanded((p) => !p)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <VpnKeyIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" fontWeight={600}>
            Issued Access Codes
          </Typography>
          {codes.length > 0 && (
            <Chip label={codes.length} size="small" color="primary" />
          )}
        </Box>
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : codes.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
              No access codes issued yet. Codes are created automatically when tenants are added.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Lock</TableCell>
                  <TableCell>Issued To</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {codes.map((c) => (
                  <TableRow key={c.codeId}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          fontWeight={700}
                          letterSpacing={2}
                        >
                          {c.code}
                        </Typography>
                        <Tooltip title={copied === c.code ? "Copied!" : "Copy"}>
                          <IconButton size="small" onClick={() => handleCopy(c.code)}>
                            {copied === c.code ? (
                              <CheckCircleIcon fontSize="small" color="success" />
                            ) : (
                              <ContentCopyIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {c.isOfflineAccessCode ? (
                        <Chip
                          icon={<WifiOffIcon sx={{ fontSize: 12 }} />}
                          label="Offline algoPIN"
                          size="small"
                          variant="outlined"
                          color="warning"
                        />
                      ) : (
                        <Chip label="Online" size="small" variant="outlined" color="info" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {c.lockId ? c.lockId.slice(-8) : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{c.tenantId || "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{formatExpiresAt(c.expiresAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={c.isActive ? "Active" : "Revoked"}
                        color={c.isActive ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── main component ───────────────────────────────────────────────────────────

const TenantsList = ({
  propertyId,
  refreshTrigger,
  onTenantCountChange,
  onAddTenant,
}) => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const [editTarget, setEditTarget] = useState(null);
  const [editLeaseEnd, setEditLeaseEnd] = useState("");
  const [editStatus, setEditStatus] = useState(true);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState("");

  // ── fetch ──────────────────────────────────────────────────────────────────
  const fetchTenants = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getTenants(propertyId);

      // Check each tenant email against the users collection for userType "renter"
      const enriched = await Promise.all(
        data.map(async (tenant) => {
          try {
            const usersSnap = await getDocs(
              query(
                collection(db, "users"),
                where("email", "==", tenant.email),
                where("userType", "==", "renter")
              )
            );
            return { ...tenant, isRegistered: !usersSnap.empty };
          } catch {
            return { ...tenant, isRegistered: false };
          }
        })
      );

      setTenants(enriched);
      onTenantCountChange?.(enriched.length);
    } catch (err) {
      console.error("[TenantsList] fetch error:", err);
      setError("Failed to load tenants.");
    } finally {
      setLoading(false);
    }
  }, [propertyId, onTenantCountChange]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants, refreshTrigger]);

  // ── delete ─────────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteSubmitting(true);
    try {
      await deleteTenant(propertyId, deleteTarget.id);
      const updated = tenants.filter((t) => t.id !== deleteTarget.id);
      setTenants(updated);
      onTenantCountChange?.(updated.length);
      setDeleteTarget(null);
    } catch (err) {
      console.error("[TenantsList] delete error:", err);
      setError("Failed to remove tenant.");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="h6">
          Tenants{" "}
          <Box component="span" sx={{ fontSize: 14, color: "text.secondary", fontWeight: 400 }}>
            ({tenants.length})
          </Box>
        </Typography>

        {onAddTenant && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={onAddTenant}>
            Add Tenant
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tenants table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : tenants.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography variant="body2" color="text.secondary">
            No tenants added yet. Click "Add Tenant" to get started.
          </Typography>
        </Box>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Lease Start</TableCell>
              <TableCell>Lease End</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenants.map((tenant) => {
              const active =
                tenant.status && isLeaseActive(tenant.leaseStartDate, tenant.leaseEndDate);
              return (
                <TableRow key={tenant.id}>
                  <TableCell>{tenant.email}</TableCell>
                  <TableCell>{formatDate(tenant.leaseStartDate)}</TableCell>
                  <TableCell>{formatDate(tenant.leaseEndDate)}</TableCell>
                  <TableCell>
                    <Chip
                      label={tenant.isRegistered ? "Registered" : "Invite Sent"}
                      color={tenant.isRegistered ? "success" : "default"}
                      size="small"
                      variant="outlined"
                      style={{ color: tenant.isRegistered ? "white" : "" }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={active ? "Active" : "Inactive"}
                      color={active ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => {
                        setEditTarget(tenant);
                        setEditLeaseEnd(tenant.leaseEndDate || "");
                        setEditStatus(tenant.status ?? true);
                        setEditError("");
                      }}
                      sx={{ mr: 0.5 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => setDeleteTarget(tenant)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* Issued codes panel */}
      {propertyId && <PermanentCodesPanel propertyId={propertyId} />}

      {/* Delete dialog */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleteSubmitting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove Tenant</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to permanently remove{" "}
            <strong>{deleteTarget?.email}</strong>? This will revoke their
            tenant access to this property.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleteSubmitting}
            startIcon={
              deleteSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deleteSubmitting ? "Removing…" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog
        open={!!editTarget}
        onClose={() => !editSubmitting && setEditTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit Tenant Details</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Email Address"
              value={editTarget?.email || ""}
              fullWidth
              disabled
              size="small"
            />
            <TextField
              label="Lease Start Date"
              type="date"
              value={editTarget?.leaseStartDate || ""}
              onChange={(e) =>
                setEditTarget((prev) => ({ ...prev, leaseStartDate: e.target.value }))
              }
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: editLeaseEnd || undefined }}
            />
            <TextField
              label="Lease End Date"
              type="date"
              value={editLeaseEnd}
              onChange={(e) => setEditLeaseEnd(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: editTarget?.leaseStartDate || undefined }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: "1px solid",
                borderColor: editStatus ? "divider" : "error.main",
                borderRadius: 1,
                px: 2,
                py: 1,
                bgcolor: editStatus ? "transparent" : "error.50",
                transition: "all 0.2s",
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Revoke Tenant Access
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {editStatus
                    ? "Tenant currently has access"
                    : "Access is revoked — tenant cannot log in"}
                </Typography>
              </Box>
              <Switch
                checked={!editStatus}
                onChange={(e) => setEditStatus(!e.target.checked)}
                color="error"
              />
            </Box>

            {editError && (
              <Alert severity="error" onClose={() => setEditError("")}>
                {editError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)} disabled={editSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={editSubmitting || !editLeaseEnd}
            onClick={async () => {
              setEditSubmitting(true);
              setEditError("");
              try {
                await updateTenant(propertyId, editTarget.email, {
                  leaseStartDate: editTarget.leaseStartDate,
                  leaseEndDate: editLeaseEnd,
                  status: editStatus,
                });
                setEditTarget(null);
                await fetchTenants();
              } catch (err) {
                console.error("[TenantsList] update error:", err);
                setEditError(err.message || "Failed to update tenant.");
              } finally {
                setEditSubmitting(false);
              }
            }}
            startIcon={
              editSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <EditIcon />
              )
            }
          >
            {editSubmitting ? "Saving…" : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantsList;